import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useRedux from 'hooks/useRedux'
import { useSelector } from 'react-redux'
import { ApplicationState } from 'store'
import {
  CardKanban,
  KANBAN,
  KanbanBoardWithEtapas,
  PerfilColor,
  perfilMap
} from 'store/modules/kanban/types'
import * as kanbanActions from 'store/modules/kanban/actions'
import * as atendimentoActions from 'store/modules/atendimento/actions'
import {
  ActionButton,
  ActionButtonsContainer,
  BoardContainer,
  CardBadge,
  CardContainer,
  CardHeader,
  CardInfo,
  CardTag,
  CardTime,
  CardTitle,
  CardValue,
  ColumnHeader,
  ColumnTitle,
  ContentFiltros,
  DescriptionKanban,
  TitleKanban
} from '../style'
import InputCustom from 'components/Inputs'
import MUIAvatar from '@mui/material/Avatar'
import { Badge, Button, Col, Row } from 'reactstrap'
import { MdOutlineWatchLater } from 'react-icons/md'
import { ControlledBoard } from '@caldwell619/react-kanban'
import { FaClock, FaEnvelope, FaPhone } from 'react-icons/fa'
import { maskCPF, maskMoney, maskPhoneWithDDD, removeMaskCPF } from 'util/masks'
import { CiFilter } from 'react-icons/ci'
import ModalCard from '../components/ModalCard'
import { IconButton, Tooltip } from '@mui/material'
import { GoPersonFill } from 'react-icons/go'
import { CustomButton } from 'styles/global'
import { FiTarget } from 'react-icons/fi'
import ModalCadastroCliente from './ModalCadastroCliente'
import ModalCpf from './ModalCpf'
import ModalMotivo from './ModalMotivo'
import ModalAgendamento from './ModalAgendamento'
import {
  LuClockAlert,
  LuFolderArchive,
  LuListCheck,
  LuRefreshCw,
  LuUserPlus,
  LuUsers
} from 'react-icons/lu'
import CustomSelect from 'components/Select'
import ModalConfirmacao from './ModalConfirmacao'
import ModalChatwoot from './ModalChatwoot'
import ModalDiscagem from './ModalDiscagem'
import loadingIcon from 'assets/svg/loading-icon.svg'

const MOVE_THRESHOLD = 3
const Kanban = () => {
  const {
    kanbanData,
    filtros,
    modalKanban,
    bigNumbers,
    optionsSelect,
    loadingSelect,
    coordenador,
    isOpenModalMotivo,
    isModalChatwootOpen,
    loadingKanban,
    isOpenModalAgendamento,
    discagem
  } = useSelector<ApplicationState, KANBAN>((state) => state.kanban)

  const { dispatch } = useRedux()

  const [board, setBoard] =
    useState<KanbanBoardWithEtapas<CardKanban>>(kanbanData)
  const [searchText, setSearchText] = useState<string>('')
  const [isOverDue, setIsOverDue] = useState<boolean>(false)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [invalidColumns, setInvalidColumns] = useState<string[] | null>(null)
  const [showTagsDropdown, setShowTagsDropdown] = useState<boolean>(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedPeople, setSelectedPeople] = useState<string[]>([])
  const [doubleClickTimer, setDoubleClickTimer] =
    useState<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const boardScrollRafRef = useRef<number | null>(null)
  const boardContainerRef = useRef<HTMLDivElement>(null)

  const rafRef = useRef<number | null>(null)
  const mountedRef = useRef<boolean>(true)

  const draggingRef = useRef<boolean>(false)

  const dragStartRef = useRef<{
    cardId: string | number
    startX: number
    startY: number
    originColumnId?: string | number
    originPosition?: number
  } | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      if (boardScrollRafRef.current !== null) {
        cancelAnimationFrame(boardScrollRafRef.current)
        boardScrollRafRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const container = boardContainerRef.current
    if (!container) return

    const updateHeaderHeight = () => {
      const header = container.querySelector(
        '.kanban-column-header'
      ) as HTMLElement | null
      if (!header) return
      const styles = window.getComputedStyle(header)
      const marginBottom = parseFloat(styles.marginBottom || '0')
      const height = header.offsetHeight + marginBottom
      container.style.setProperty('--kanban-header-height', `${height}px`)
    }

    const rafId = requestAnimationFrame(updateHeaderHeight)
    window.addEventListener('resize', updateHeaderHeight)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateHeaderHeight)
    }
  }, [board])

  const handleBoardScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (boardScrollRafRef.current !== null) return
    boardScrollRafRef.current = requestAnimationFrame(() => {
      boardScrollRafRef.current = null
      const offset = Math.max(0, target.scrollTop - 11)
      target.style.setProperty('--kanban-header-offset', `${offset}px`)
    })
  }, [])

  const schedule = useCallback((fn: () => void) => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      if (mountedRef.current) fn()
    })
  }, [])

  const getKanbanData = useCallback(() => {
    if (filtros.pipelineId) {
      dispatch(kanbanActions.getKanbanSaga())
      dispatch(kanbanActions.getBigNumbersSaga(filtros.pipelineId))
    }
  }, [dispatch, filtros.pipelineId])

  useEffect(() => {
    if (kanbanData.centroCustoId) {
      dispatch(
        kanbanActions.getConsultoresModalKanbanSaga(kanbanData.centroCustoId)
      )
    }
  }, [dispatch, kanbanData.centroCustoId])

  useEffect(() => {
    dispatch(kanbanActions.getCentrosCustoSaga())
  }, [dispatch])

  useEffect(() => {
    if (filtros.pipelineId) {
      getKanbanData()
    }
  }, [filtros.pipelineId, filtros.isArchived, getKanbanData])

  useEffect(() => {
    if (kanbanData) setBoard(kanbanData)
  }, [kanbanData])

  const calculateTimespan = useCallback((time: string): string => {
    const now = new Date()
    const pastDate = new Date(time)
    const diff = Math.abs(now.getTime() - pastDate.getTime())

    const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365))
    const months = Math.floor((diff / (1000 * 60 * 60 * 24 * 30)) % 12)
    const days = Math.floor((diff / (1000 * 60 * 60 * 24)) % 30)
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
    const minutes = Math.floor((diff / (1000 * 60)) % 60)

    if (years > 0) return `há ${years} ano${years > 1 ? 's' : ''}`
    if (months > 0) return `há ${months} mês${months > 1 ? 'es' : ''}`
    if (days > 0) return `há ${days} dia${days > 1 ? 's' : ''}`
    if (hours > 0) return `há ${hours} hora${hours > 1 ? 's' : ''}`
    return `há ${minutes} minuto${minutes > 1 ? 's' : ''}`
  }, [])

  const filteredBoard = useMemo(() => {
    const txt = (searchText || '').toLowerCase()

    return {
      ...board,
      columns: board?.columns.map((column) => ({
        ...column,
        cards: column.cards
          .filter((card) => {
            const titleMatches = (card.title || '').toLowerCase().includes(txt)
            const emailMatches = (card.email || '').toLowerCase().includes(txt)
            const telefoneMatches = (card.telefone || '').includes(searchText)
            const cpfMatches = (card.cpf || '').includes(
              removeMaskCPF(searchText)
            )
            return titleMatches || emailMatches || telefoneMatches || cpfMatches
          })
          .filter((card) =>
            selectedTags.length > 0
              ? Array.isArray(card.perfis) &&
                card.perfis.some((perfil) => selectedTags.includes(perfil))
              : true
          )
          .filter((card) =>
            selectedPeople.length > 0
              ? Boolean(card.consultorId) &&
                selectedPeople.includes(String(card.consultorId))
              : true
          )
          .filter((card) => {
            if (!isOverDue) return true
            const situacao = String(card.situacao || '').toLowerCase()
            return situacao.includes('atrasado')
          })
      }))
    }
  }, [board, searchText, selectedTags, selectedPeople, isOverDue])

  const peopleList = useMemo(() => {
    const map = new Map<string, string>()
    board?.columns.forEach((col) => {
      col.cards.forEach((card) => {
        if (card.consultorId && card.nomeConsultor)
          map.set(String(card.consultorId), card.nomeConsultor)
      })
    })
    return Array.from(map.entries()).map(([consultorId, nomeConsultor]) => ({
      consultorId,
      nomeConsultor
    }))
  }, [board])

  const getInitials = (nomeConsultor = '') => {
    return nomeConsultor
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join('')
  }

  type DragPosition = { fromPosition: number; fromColumnId: string }
  type DropPosition = { toPosition: number; toColumnId: string }

  const handleCardDragEnd = useCallback(
    (card: CardKanban, from: DragPosition, to: DropPosition) => {
      setIsDragging(false)
      setInvalidColumns(null)
      draggingRef.current = false
      dragStartRef.current = null

      const fromColumnCheck = board.columns.find(
        (c) => c.id === from.fromColumnId
      )
      const toColumnCheck = board.columns.find((c) => c.id === to.toColumnId)

      if (!fromColumnCheck || !toColumnCheck) return

      const permitidoNoCheck =
        !fromColumnCheck.etapasPermitidas ||
        fromColumnCheck.etapasPermitidas.some((etapa) => {
          const etapaPermitidaId =
            (etapa as { etapaId?: string; id?: string }).etapaId ||
            (etapa as { etapaId?: string; id?: string }).id

          return String(etapaPermitidaId) === String(to.toColumnId)
        })

      if (!permitidoNoCheck) {
        console.warn(
          `Movimentação proibida de ${fromColumnCheck.title} para ${toColumnCheck.title}`
        )
        return
      }

      const colunaDestino = kanbanData.columns.find(
        (col) => String(col.id) === String(to.toColumnId)
      )
      const requerMotivo = colunaDestino?.modalMotivo || false

      if (!requerMotivo) {
        setBoard((prevBoard) => {
          if (!prevBoard) return prevBoard

          const fromColumn = prevBoard.columns.find(
            (c) => c.id === from.fromColumnId
          )
          const toColumn = prevBoard.columns.find((c) => c.id === to.toColumnId)

          if (!fromColumn || !toColumn) return prevBoard

          const newColumns = prevBoard.columns.map((c) => ({
            ...c,
            cards: [...c.cards]
          }))

          const fromIndex = newColumns.findIndex(
            (c) => String(c.id) === String(from.fromColumnId)
          )
          const toIndex = newColumns.findIndex(
            (c) => String(c.id) === String(to.toColumnId)
          )

          if (fromIndex === -1 || toIndex === -1) return prevBoard

          if (fromIndex === toIndex) {
            const cards = newColumns[fromIndex].cards

            if (
              from.fromPosition === to.toPosition ||
              from.fromPosition === to.toPosition - 1
            ) {
              return prevBoard
            }
            const [moved] = cards.splice(from.fromPosition, 1)
            const insertIndex =
              to.toPosition > from.fromPosition
                ? to.toPosition - 1
                : to.toPosition
            cards.splice(insertIndex, 0, moved)
            newColumns[fromIndex] = { ...newColumns[fromIndex], cards }
          } else {
            const [moved] = newColumns[fromIndex].cards.splice(
              from.fromPosition,
              1
            )
            newColumns[toIndex].cards.splice(to.toPosition, 0, moved)
          }

          return { ...prevBoard, columns: newColumns }
        })
      }

      const payload = {
        card: card,
        from: {
          fromPosition: from.fromPosition,
          fromColumnId: String(from.fromColumnId)
        },
        to: {
          toPosition: to.toPosition,
          toColumnId: String(to.toColumnId)
        }
      }

      if (
        from.fromColumnId !== undefined &&
        to.toColumnId !== undefined &&
        board.pipelineId
      ) {
        dispatch(kanbanActions.verificarMotivoMovimentacaoSaga(payload))
      }
    },
    [board.columns, board.pipelineId, dispatch, kanbanData.columns]
  )

  useEffect(() => {
    const clearDragVisuals = () => {
      if (!dragStartRef.current && !draggingRef.current) return

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }

      setTimeout(() => {
        requestAnimationFrame(() => {
          if (!mountedRef.current) return
          setIsDragging(false)
          setInvalidColumns(null)
          draggingRef.current = false
          dragStartRef.current = null
        })
      }, 0)
    }

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' || ev.key === 'Esc') clearDragVisuals()
    }

    window.addEventListener('pointerup', clearDragVisuals)
    window.addEventListener('pointercancel', clearDragVisuals)
    window.addEventListener('mouseup', clearDragVisuals)
    window.addEventListener('touchend', clearDragVisuals)
    window.addEventListener('touchcancel', clearDragVisuals)
    window.addEventListener('drop', clearDragVisuals)
    window.addEventListener('dragend', clearDragVisuals)
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') clearDragVisuals()
    })
    window.addEventListener('blur', clearDragVisuals)
    window.addEventListener('mouseleave', clearDragVisuals)
    window.addEventListener('keydown', onKey)

    return () => {
      window.removeEventListener('pointerup', clearDragVisuals)
      window.removeEventListener('pointercancel', clearDragVisuals)
      window.removeEventListener('mouseup', clearDragVisuals)
      window.removeEventListener('touchend', clearDragVisuals)
      window.removeEventListener('touchcancel', clearDragVisuals)
      window.removeEventListener('drop', clearDragVisuals)
      window.removeEventListener('dragend', clearDragVisuals)

      window.removeEventListener('blur', clearDragVisuals)
      window.removeEventListener('mouseleave', clearDragVisuals)
      window.removeEventListener('keydown', onKey)
    }
  }, [schedule])

  const handleCardClick = useCallback(
    (cardId: string) => {
      if (doubleClickTimer) {
        clearTimeout(doubleClickTimer)
        setDoubleClickTimer(null)
        return
      }

      const timer = setTimeout(() => {
        dispatch(kanbanActions.getInformacoesCardKanbanSaga(cardId))
        setDoubleClickTimer(null)
      }, 300)

      setDoubleClickTimer(timer)
    },
    [dispatch, doubleClickTimer]
  )

  const handleCpfDoubleClick = useCallback(
    (cpf: string) => {
      if (doubleClickTimer) {
        clearTimeout(doubleClickTimer)
        setDoubleClickTimer(null)
      }
      dispatch(atendimentoActions.getFormularioAtendimento30Saga(maskCPF(cpf)))
    },
    [dispatch, doubleClickTimer]
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, card: CardKanban) => {
      if (e.button && e.button !== 0) return

      if (dragStartRef.current || draggingRef.current) return

      const startX = (e as unknown as PointerEvent).clientX
      const startY = (e as unknown as PointerEvent).clientY

      const originCol = board.columns.find((col) =>
        col.cards.some((c) => c.id === card.id)
      )
      const originPos = originCol
        ? originCol.cards.findIndex((c) => c.id === card.id)
        : -1

      dragStartRef.current = {
        cardId: card.id,
        startX,
        startY,
        originColumnId: originCol?.id,
        originPosition: originPos
      }

      const onPointerMove = (moveEvt: PointerEvent) => {
        const start = dragStartRef.current
        if (!start) return
        const dx = Math.abs((moveEvt.clientX || 0) - start.startX)
        const dy = Math.abs((moveEvt.clientY || 0) - start.startY)
        const moved = dx >= MOVE_THRESHOLD || dy >= MOVE_THRESHOLD
        if (!moved) return

        draggingRef.current = true

        const origin = board.columns.find((col) =>
          col.cards.some((c) => c.id === start.cardId)
        )
        const etapasPermitidas =
          origin?.etapasPermitidas?.map((e) => String(e.etapaId)) ?? []
        const invalid = board.columns
          .map((c) => String(c.id))
          .filter((id) => !etapasPermitidas.includes(id))

        schedule(() => {
          setIsDragging(true)
          setInvalidColumns(invalid)
        })

        window.removeEventListener('pointermove', onPointerMove)
      }

      const onPointerUp = () => {
        if (!draggingRef.current) {
          dragStartRef.current = null
        }
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)
      }

      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', onPointerUp)
    },
    [board, schedule]
  )

  return (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <div className="d-flex gap-3 align-items-center">
          <div className="d-flex flex-column">
            <TitleKanban>{kanbanData.nome}</TitleKanban>
            <DescriptionKanban>{kanbanData.descricao}</DescriptionKanban>
          </div>
          {filtros.pipelineId && (
            <div>
              <IconButton size="small" onClick={getKanbanData}>
                <LuRefreshCw />
              </IconButton>
            </div>
          )}
        </div>
      </div>
      <div className="d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-4">
            <Tooltip title="Total de leads">
              <div className="d-flex align-items-center gap-2">
                <LuUsers color="#00ff15" />
                <span>{bigNumbers.totalLeads}</span>
              </div>
            </Tooltip>

            <Tooltip title="Total de leads atrasados">
              <div className="d-flex align-items-center gap-2">
                <LuClockAlert color="#d68204" />
                <span>{bigNumbers.totalLeadsAtraso}</span>
              </div>
            </Tooltip>

            <Tooltip title="Total de novos leads">
              <div className="d-flex align-items-center gap-2">
                <LuUserPlus color="#007bff" />
                <span>{bigNumbers.totalNovosLeads}</span>
              </div>
            </Tooltip>

            <Tooltip title="Total de tarefas para o consultor hoje">
              <div className="d-flex align-items-center gap-2">
                <LuListCheck color="#0c7006" />
                <span>{bigNumbers.totalTarefasConsultorHoje}</span>
              </div>
            </Tooltip>
          </div>
          <div>
            {!kanbanData.site && (
              <CustomButton
                className="d-flex gap-2"
                background="var(--roxo-riber)"
                onClick={() => {
                  dispatch(kanbanActions.setIsOpenModalCpf(true))
                }}
              >
                <FiTarget className="icon" />
                <span>Adicionar cliente</span>
              </CustomButton>
            )}
          </div>
        </div>
        <ContentFiltros>
          <form autoComplete="off">
            <InputCustom
              type="text"
              placeholder="Buscar por nome, CPF ou telefone..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </form>
          <div className="d-flex gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              {peopleList.slice(0, 20).map((p) => {
                const active = selectedPeople.includes(p.consultorId)
                return (
                  <button
                    key={p.consultorId}
                    type="button"
                    title={p.nomeConsultor}
                    onClick={() =>
                      setSelectedPeople((prev) =>
                        prev.includes(p.consultorId)
                          ? prev.filter((x) => x !== p.consultorId)
                          : [...prev, p.consultorId]
                      )
                    }
                    style={{
                      border: 'none',
                      padding: 0,
                      background: 'transparent',
                      cursor: 'pointer'
                    }}
                  >
                    <MUIAvatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: active ? '#007bff' : '#e9ecef',
                        color: active ? '#fff' : '#212529',
                        fontSize: 12,
                        fontWeight: 700,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {p.nomeConsultor
                        .split(' ')
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((n) => n[0].toUpperCase())
                        .join('')}
                    </MUIAvatar>
                  </button>
                )
              })}
              {peopleList.length > 20 && (
                <span style={{ fontSize: 12 }}>+{peopleList.length - 20}</span>
              )}
            </div>
            <div>
              <Button
                className="btn-filtro"
                onClick={() => setShowTagsDropdown((prev) => !prev)}
                active={selectedTags.length > 0}
              >
                <CiFilter />
                <span>Tags</span>
              </Button>
              {showTagsDropdown && (
                <div ref={dropdownRef} className="tags-dropdown">
                  <p>Filtrar por tags</p>
                  {[
                    'INSS',
                    'SERVIDOR PUBLICO FEDERAL',
                    'SERVIDOR PUBLICO MUNICIPAL',
                    'SERVIDOR PUBLICO ESTADUAL',
                    'CLT',
                    'AUTONOMO',
                    'BOLSA FAMILIA'
                  ].map((tag) => (
                    <div key={tag}>
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTags((prev) => [...prev, tag])
                          } else {
                            setSelectedTags((prev) =>
                              prev.filter((t) => t !== tag)
                            )
                          }
                        }}
                      />
                      <label>{tag}</label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Button
                className="btn-filtro"
                onClick={() => setIsOverDue((prev) => !prev)}
                active={isOverDue}
              >
                <MdOutlineWatchLater />
                <span>Atrasadas</span>
              </Button>
            </div>
            <div>
              <Button
                className="btn-filtro"
                onClick={() => {
                  dispatch(
                    kanbanActions.setFiltrosField(
                      !filtros.isArchived,
                      'isArchived'
                    )
                  )
                }}
                active={filtros.isArchived}
              >
                <LuFolderArchive />
                <span>Arquivadas</span>
              </Button>
            </div>
            {coordenador && (
              <div>
                <CustomSelect
                  value={filtros.centroCustoId || ''}
                  onChange={(e) => {
                    dispatch(
                      kanbanActions.setFiltrosField(e || null, 'centroCustoId')
                    )
                    if (e) {
                      dispatch(kanbanActions.getPipelinesSaga(e, false))
                    }
                  }}
                  placeholder="Selecione o Centro de Custo"
                  options={optionsSelect.centrosCusto}
                  isLoading={loadingSelect.centrosCusto}
                  accessorLabel="descricao"
                  accessorValue="id"
                />
              </div>
            )}
            <div>
              <CustomSelect
                value={filtros.pipelineId || ''}
                onChange={(e) =>
                  dispatch(
                    kanbanActions.setFiltrosField(e || null, 'pipelineId')
                  )
                }
                placeholder="Selecione o Pipeline"
                options={optionsSelect.pipelines}
                isLoading={loadingSelect.pipelines}
                accessorLabel="descricao"
                accessorValue="id"
              />
            </div>
          </div>
        </ContentFiltros>
        {loadingKanban ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <img src={loadingIcon} alt="Loading sistema" width="100" />
          </div>
        ) : (
          <BoardContainer ref={boardContainerRef} onScroll={handleBoardScroll}>
            <ControlledBoard
              onCardDragEnd={(card, from, to) => {
                if (rafRef.current !== null) {
                  cancelAnimationFrame(rafRef.current)
                  rafRef.current = null
                }
                setIsDragging(false)
                setInvalidColumns(null)
                draggingRef.current = false
                dragStartRef.current = null

                if (
                  card === undefined ||
                  from?.fromColumnId === undefined ||
                  from?.fromPosition === undefined ||
                  to?.toColumnId === undefined ||
                  to?.toPosition === undefined
                ) {
                  return
                }

                handleCardDragEnd(
                  card,
                  {
                    fromPosition: from.fromPosition,
                    fromColumnId: String(from.fromColumnId)
                  },
                  {
                    toPosition: to.toPosition + 1,
                    toColumnId: String(to.toColumnId)
                  }
                )
              }}
              disableColumnDrag
              allowRemoveColumn={false}
              allowRemoveCard={false}
              allowRenameColumn={false}
              allowAddCard={false}
              renderCard={(card, options) => {
                return (
                  <CardContainer
                    borderColor={card.borderColor}
                    data-dragging={options.dragging ? 'true' : 'false'}
                    onPointerDown={(e) => handlePointerDown(e, card)}
                    onClick={() => handleCardClick(String(card.id ?? ''))}
                  >
                    <CardHeader>
                      <div className="d-flex align-items-center gap-2">
                        <MUIAvatar
                          title={card.nomeConsultor || ''}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'var(--verde-riber)',
                            color: 'var(--branco)',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'default',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {card.nomeConsultor
                            ? getInitials(card.nomeConsultor)
                            : ''}
                        </MUIAvatar>
                        <div className="d-flex flex-column gap-1">
                          <CardTitle>{card.title}</CardTitle>
                          <div className="d-flex flex-wrap gap-1">
                            {card.perfis &&
                              (() => {
                                const MAX_VISIBLE = 2
                                const visible = card.perfis.slice(
                                  0,
                                  MAX_VISIBLE
                                )
                                const hidden =
                                  card.perfis.length - visible.length
                                return (
                                  <>
                                    {visible.map((perfil, i) => (
                                      <CardTag
                                        key={`${card.clienteId}-${perfil}-${i}`}
                                        color={
                                          PerfilColor[perfilMap[perfil]] ||
                                          '#000'
                                        }
                                      >
                                        {perfil}
                                      </CardTag>
                                    ))}

                                    {hidden > 0 && (
                                      <CardTag
                                        title={card.perfis
                                          .slice(MAX_VISIBLE)
                                          .join(', ')}
                                        color="#6c757d"
                                      >
                                        +{hidden}
                                      </CardTag>
                                    )}
                                  </>
                                )
                              })()}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <Row>
                      <Col className="d-flex align-items-center gap-2">
                        <FaPhone size={12} />
                        <CardInfo>{maskPhoneWithDDD(card.telefone)}</CardInfo>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="d-flex align-items-center gap-2">
                        <FaEnvelope size={12} />{' '}
                        <CardInfo>{card.email}</CardInfo>
                      </Col>
                    </Row>
                    <Row
                      className="pointer"
                      onDoubleClick={() => handleCpfDoubleClick(card.cpf)}
                    >
                      <Col className="d-flex align-items-center gap-2">
                        <GoPersonFill size={12} />
                        <CardInfo>{maskCPF(card.cpf)}</CardInfo>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {card.situacao === 'Em dia' ? (
                          <CardBadge color="#97d399">Em dia</CardBadge>
                        ) : (
                          <CardBadge>Atrasado</CardBadge>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="d-flex align-items-center gap-2">
                        <CardValue>
                          R$&nbsp;
                          {card.value
                            ? maskMoney(card.value.toFixed(2))
                            : maskMoney(0)}
                        </CardValue>
                      </Col>
                      <Col className="d-flex align-items-center justify-content-end gap-2">
                        <FaClock size={12} />
                        <CardTime>{calculateTimespan(card.time)}</CardTime>
                      </Col>
                    </Row>

                    <ActionButtonsContainer className="action-buttons-container">
                      <ActionButton>Ligar</ActionButton>
                      <ActionButton>WA</ActionButton>
                      <ActionButton>Email</ActionButton>
                    </ActionButtonsContainer>
                  </CardContainer>
                )
              }}
              renderColumnHeader={(column) => {
                const invalid =
                  isDragging &&
                  invalidColumns &&
                  invalidColumns.includes(String(column.id))

                return (
                  <ColumnHeader
                    className="kanban-column-header"
                    invalid={invalid}
                  >
                    <ColumnTitle>{column.title}</ColumnTitle>
                    <Badge color="success" pill>
                      {column.cards.length}
                    </Badge>
                  </ColumnHeader>
                )
              }}
            >
              {filteredBoard}
            </ControlledBoard>
          </BoardContainer>
        )}
      </div>
      <ModalCard
        isOpen={modalKanban.isOpen}
        onToggle={() => {
          dispatch(kanbanActions.setIsOpenModalKanban(!modalKanban.isOpen))
        }}
      />
      <ModalCadastroCliente />
      <ModalCpf />
      <ModalMotivo isOpen={isOpenModalMotivo} />
      <ModalAgendamento isOpen={isOpenModalAgendamento} />
      <ModalConfirmacao />
      <ModalChatwoot
        isOpen={isModalChatwootOpen}
        onToggle={() => dispatch(kanbanActions.toggleChatwootModal(false))}
        infosCliente={{
          cpf: modalKanban.informacoes.cpf,
          nome: modalKanban.informacoes.nome,
          telefone: `${modalKanban.informacoes.ddd}${modalKanban.informacoes.numero}`
        }}
      />
      <ModalDiscagem
        isOpen={discagem.isOpen}
        onToggle={() => dispatch(kanbanActions.cleanModalDiscagem())}
      />
    </div>
  )
}

export default Kanban
